import merge from 'deepmerge';
import NewHomeSourcePartner from 'constants/partner';
import matchesMobileUserAgent from 'modules/deviceCapabilities';
import PageType from 'enums/pageType';
import UrlExtensions from 'utils/urlExtension';
import { selectUrlSubdirectory } from 'features/routing';
import selectTrackingCommonProperties from './redux/selectors';
import getAnalyticsTrackingProperties from './components/analyticsDataHandler';
import getAcxiomTrackingProperties from './components/acxiomDataHandler';
import { LocationType, ResultType, UrlSubdirectoryTypes, selectResultType } from '../searchResultsFacets';

/**
 * Safely parses the business information data from the DOM.
 * @returns {Object|null} The parsed business information object or null if unavailable/invalid.
 */
const parseBusinessInformation = () => {
    try {
        const businessInformationElement = document.querySelector("[data-component='business-information-data']");
        if (!businessInformationElement || !businessInformationElement.innerHTML) {
            return null;
        }
        return JSON.parse(businessInformationElement.innerHTML);
    } catch (error) {
        return null;
    }
};

/**
 * Derives the pageType from locationType (for state pages) or URL subdirectory.
 * @param {Object} state - The Redux state.
 * @param {string|undefined} locationType - The location type from Redux state.
 * @returns {string|undefined} The derived page type or undefined.
 */
const derivePageType = (state, locationType) => {
    try {
        const resultType = selectResultType(state);

        // First priority: Check if this is a state page using locationType from Redux
        if (locationType === LocationType.State.toString()) {
            if (resultType === ResultType.Communities.toString()) {
                return PageType.CommunitiesState.toString();
            }

            if (resultType === ResultType.Homes.toString()) {
                return PageType.HomesState.toString();
            }

            return PageType.State.toString();
        }

        // Second priority: Derive from URL subdirectory
        const urlSubdirectory = selectUrlSubdirectory(state) || UrlExtensions.getSubdirectory(window.location);
        if (!urlSubdirectory) {
            return undefined;
        }

        // routingState.urlSubdirectory updates on a deferred dispatch, so it can still reflect
        // the previous tab right after a click; prefer the live active tab when it disagrees.
        if (urlSubdirectory === UrlSubdirectoryTypes.Communities.toString() ||
            urlSubdirectory === UrlSubdirectoryTypes.Homes.toString()) {
            if (resultType === ResultType.Homes.toString()) {
                return PageType.Homes.toString();
            }

            if (resultType === ResultType.Communities.toString()) {
                return PageType.Communities.toString();
            }
        }

        const pageType = PageType.fromKey(urlSubdirectory);
        return pageType?.toString();
    } catch (error) {
        return undefined;
    }
};

const getResponseVersion = (isMobileDevice) => (isMobileDevice ? 'M' : 'D');

/**
 * The getCommonPropertiesFromOtherSources function returns an object containing the properties obtained from:
 * sources such as html, or constants.
 * @return An object with the properties not stored in redux.
 */
const getCommonPropertiesFromOtherSources = (store, locationType) => {
  const businessInformation = parseBusinessInformation();
  const isMobileUserAgent = matchesMobileUserAgent();
  const acxiomTrackingProperties = getAcxiomTrackingProperties();

  // Derive pageType with robust fallback logic:
  // 1. Derive from locationType (for state pages) or urlSubdirectory
  // 2. Fall back to businessInformation.pageType (when available, for backward compatibility)
  // 3. Fall back to undefined (tracking consumers should handle this gracefully)
  const pageType = derivePageType(store, locationType) || businessInformation?.pageType;

  const properties = {
    pageType,
    buildCodeVersion: businessInformation.buildCodeVersion,
    responseVersion: getResponseVersion(isMobileUserAgent),
    testVersion: NewHomeSourcePartner.testVersion,
    partnerId: NewHomeSourcePartner.partnerId,
    partnerName: NewHomeSourcePartner.partnerName,
    brandPartnerName: NewHomeSourcePartner.brandPartnerName,
    acxiomTrackingProperties
  };

  return properties;
};

/**
 * The function returns common tracking properties that are stored in the redux store.
 * @param store - The `store` parameter is likely an object representing a store or a state management
 * @returns an object containing the properties stored in a redux store.
 */
const getCommonPropertiesFromStore = (store) => {
  const trackingCommonPropertiesFromStore = selectTrackingCommonProperties(store);
  return trackingCommonPropertiesFromStore;
};

/**
 * The getTrackingCommonProperties function returns tracking common properties from the Redux store.
 * this uses a selector which takes from other features selectors a set of properties and combines them.
 * The idea is that this selector can be used for different tracking consumers such as segment, g4.
 * by a respective object builder which will filter that properties as needed.
 * @param store Access the redux store
 *
 * @return An object of properties.
 */
const getTrackingCommonProperties = (store) => {
  const trackingCommonPropertiesFromStore = getCommonPropertiesFromStore(store);
  const locationType = trackingCommonPropertiesFromStore?.locationType;
  const trackingCommonPropertiesFromOtherSources = getCommonPropertiesFromOtherSources(store, locationType);
  const trackingCommonProperties = merge(trackingCommonPropertiesFromStore, trackingCommonPropertiesFromOtherSources);
  return trackingCommonProperties;
};

export { getAnalyticsTrackingProperties };
export default getTrackingCommonProperties;